import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  FunnelIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'
import { CheckBadgeIcon } from '@heroicons/react/24/solid'
import { fetchWatsonChainRecords, getOneChainScanTxUrl } from '../services/watsonChainApi'
import { fetchMapIdentify, type MapIdentifyLocation } from '../services/mapGovApi'
import type { WatsonLedgerRow } from '../types/watsonChain'

const PAGE_SIZE = 10
/** How many extra pages to background-fetch per prefetch wave (can run again near the end). */
const PREFETCH_BATCH_SIZE = 50

/** Hardcoded demo row pinned at the top of page 1 (includes coordinates). */
const EXAMPLE_LEDGER_ROW: WatsonLedgerRow = {
  id: 'example',
  txId: '6eeb316232411ce2006652bcf40016cd2becf29ae9f8a8778e71f648388e7d9c',
  recordedAt: '2026-07-01T04:00:00.000Z',
  recordedAtMs: Date.parse('2026-07-21T04:00:00.000Z'),
  machineId: 3003,
  brand: 'Example',
  barcode: 4891234567890,
  capacityMl: 500,
  bottleCount: 3,
  weightGrams: 45,
  points: 15,
  credits: 15,
  status: 'verified',
  platformNumber: 'DEMO',
  coordinates: {
    easting_x: 839486,
    northing_y: 831991
  }
}

interface CarbonCreditLedgerProps {
  onSummaryChange?: (summary: { totalCredits: number; verifiedCredits: number; pendingCredits: number; totalRecords: number }) => void
}

const formatDisplayDate = (isoDate: string) => {
  if (!isoDate) return '—'
  const date = new Date(isoDate)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const identifyLocationCache = new Map<string, MapIdentifyLocation>()

const CoordinatesCell: React.FC<{
  coordinates: NonNullable<WatsonLedgerRow['coordinates']>
}> = ({ coordinates }) => {
  const [hovering, setHovering] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [locationInfo, setLocationInfo] = useState<MapIdentifyLocation | null>(null)
  const cacheKey = `${coordinates.easting_x},${coordinates.northing_y}`

  const loadLocationInfo = useCallback(async () => {
    const cached = identifyLocationCache.get(cacheKey)
    if (cached) {
      setLocationInfo(cached)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const result = await fetchMapIdentify(coordinates.easting_x, coordinates.northing_y)
      identifyLocationCache.set(cacheKey, result)
      setLocationInfo(result)
    } catch (loadError) {
      setLocationInfo(null)
      setError(loadError instanceof Error ? loadError.message : 'Failed to load location')
    } finally {
      setLoading(false)
    }
  }, [cacheKey, coordinates.easting_x, coordinates.northing_y])

  const handleMouseEnter = () => {
    setHovering(true)
    void loadLocationInfo()
  }

  return (
    <div
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setHovering(false)}
    >
      <span className="cursor-default border-b border-dotted border-gray-400">
        {coordinates.easting_x}, {coordinates.northing_y}
      </span>
      {hovering && (
        <div className="pointer-events-none absolute left-0 top-full z-30 mt-2 w-72">
          <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-xl">
            {loading && (
              <div className="text-xs text-gray-500">Loading location...</div>
            )}
            {!loading && error && (
              <div className="text-xs text-red-600">{error}</div>
            )}
            {!loading && !error && locationInfo && (
              <div>
                <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-2 text-xs">
                  <span className="text-right text-gray-500">ID:</span>
                  <span className="text-left font-medium text-gray-900">{locationInfo.id}</span>
                  <span className="text-right text-gray-500">Name:</span>
                  <span className="text-left font-medium text-gray-900">{locationInfo.name}</span>
                  <span className="text-right text-gray-500">Address:</span>
                  <span className="text-left font-medium text-gray-900">{locationInfo.address || '—'}</span>
                </div>
                <p className="mt-2 flex items-center gap-1 border-t border-gray-100 pt-2 text-[10px] leading-snug text-gray-400">
                  <CheckBadgeIcon className="h-3.5 w-3.5 shrink-0 text-sky-500" aria-hidden="true" />
                  by CSDI Lab
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const CarbonCreditLedger: React.FC<CarbonCreditLedgerProps> = ({ onSummaryChange }) => {
  const [records, setRecords] = useState<WatsonLedgerRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)
  const [source, setSource] = useState<'onechain' | 'fallback'>('onechain')
  const [fetchedAt, setFetchedAt] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'verified' | 'pending' | 'failed'>('all')
  const [sortBy, setSortBy] = useState<'date' | 'credits' | 'machine'>('date')
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [reachablePages, setReachablePages] = useState<number[]>([1])

  // bookmark used to fetch each page: page 1 => '', page 2 => newBookmark from page 1, etc.
  const pageBookmarksRef = useRef<Record<number, string>>({ 1: '' })
  const pageCacheRef = useRef<Record<number, WatsonLedgerRow[]>>({})
  const hasMoreByPageRef = useRef<Record<number, boolean>>({})
  const fetchingPagesRef = useRef<Set<number>>(new Set())
  const prefetchGenerationRef = useRef(0)
  const prefetchInFlightRef = useRef(false)
  const pageBannerRef = useRef<HTMLDivElement>(null)

  const syncReachablePages = useCallback(() => {
    const pages = Object.keys(pageBookmarksRef.current)
      .map(Number)
      .filter((n) => Number.isInteger(n) && n > 0)
      .sort((a, b) => a - b)
    setReachablePages(pages.length > 0 ? pages : [1])
  }, [])

  const prefetchAhead = useCallback(async () => {
    if (prefetchInFlightRef.current) return
    prefetchInFlightRef.current = true
    const generation = prefetchGenerationRef.current
    let fetchedInBatch = 0

    try {
      while (
        generation === prefetchGenerationRef.current &&
        fetchedInBatch < PREFETCH_BATCH_SIZE
      ) {
        const bookmarkPages = Object.keys(pageBookmarksRef.current)
          .map(Number)
          .filter((n) => Number.isInteger(n) && n > 0)
          .sort((a, b) => a - b)

        const nextToFetch = bookmarkPages.find(
          (pageNumber) =>
            !pageCacheRef.current[pageNumber] && !fetchingPagesRef.current.has(pageNumber)
        )
        if (nextToFetch === undefined) break

        const bookmark = pageBookmarksRef.current[nextToFetch]
        if (bookmark === undefined) break

        fetchingPagesRef.current.add(nextToFetch)
        try {
          const payload = await fetchWatsonChainRecords({
            paginated: true,
            pageSize: PAGE_SIZE,
            bookmark
          })

          if (generation !== prefetchGenerationRef.current) break

          const pageHasMore =
            payload.records.length >= PAGE_SIZE && Boolean(payload.pagination?.hasMore)

          if (payload.records.length === 0) {
            delete pageCacheRef.current[nextToFetch]
            delete pageBookmarksRef.current[nextToFetch]
            if (nextToFetch > 1) {
              hasMoreByPageRef.current[nextToFetch - 1] = false
            }
            syncReachablePages()
            break
          }

          pageCacheRef.current[nextToFetch] = payload.records
          hasMoreByPageRef.current[nextToFetch] = pageHasMore
          fetchedInBatch += 1

          if (pageHasMore && payload.pagination?.newBookmark) {
            pageBookmarksRef.current[nextToFetch + 1] = payload.pagination.newBookmark
          } else {
            delete pageBookmarksRef.current[nextToFetch + 1]
          }

          syncReachablePages()

          if (!pageHasMore) break
        } finally {
          fetchingPagesRef.current.delete(nextToFetch)
        }
      }
    } catch {
      // Background prefetch failures should not disrupt the visible page.
    } finally {
      prefetchInFlightRef.current = false
    }
  }, [syncReachablePages])

  const loadPage = useCallback(async (targetPage: number, forceRefresh = false) => {
    if (targetPage > 1 && pageBookmarksRef.current[targetPage] === undefined) {
      setError('Next page is not ready yet. Please wait for the current page to finish loading.')
      return
    }

    if (!forceRefresh && pageCacheRef.current[targetPage]) {
      setRecords(pageCacheRef.current[targetPage])
      setHasMore(hasMoreByPageRef.current[targetPage] ?? false)
      syncReachablePages()
      setLoading(false)
      void prefetchAhead()
      return
    }

    setLoading(true)
    setError(null)
    fetchingPagesRef.current.add(targetPage)

    try {
      const bookmark = pageBookmarksRef.current[targetPage] ?? ''
      const payload = await fetchWatsonChainRecords({
        refresh: forceRefresh,
        paginated: true,
        pageSize: PAGE_SIZE,
        bookmark
      })

      const pageHasMore =
        payload.records.length >= PAGE_SIZE && Boolean(payload.pagination?.hasMore)

      if (payload.records.length === 0 && targetPage > 1) {
        delete pageCacheRef.current[targetPage]
        delete pageBookmarksRef.current[targetPage]
        hasMoreByPageRef.current[targetPage - 1] = false
        setHasMore(false)
        setRecords([])
        setWarning('No more records — you have reached the last page.')
        setLoading(false)
        syncReachablePages()
        setPage(targetPage - 1)
        return
      }

      pageCacheRef.current[targetPage] = payload.records
      hasMoreByPageRef.current[targetPage] = pageHasMore

      if (pageHasMore && payload.pagination?.newBookmark) {
        pageBookmarksRef.current[targetPage + 1] = payload.pagination.newBookmark
      } else {
        delete pageBookmarksRef.current[targetPage + 1]
      }

      setRecords(payload.records)
      setSource(payload.source)
      setWarning(payload.warning || null)
      setFetchedAt(payload.fetchedAt)
      setHasMore(pageHasMore)
      syncReachablePages()
      void prefetchAhead()
    } catch (loadError) {
      setRecords([])
      const message = loadError instanceof Error ? loadError.message : 'Failed to load Watson chain records'
      setError(
        message.includes('500') || message.includes('Failed to fetch')
          ? `${message}. Make sure the API backend is running: npm run dev:api (port 4002).`
          : message
      )
    } finally {
      fetchingPagesRef.current.delete(targetPage)
      setLoading(false)
    }
  }, [prefetchAhead, syncReachablePages])

  useEffect(() => {
    void loadPage(page, false)
  }, [page, loadPage])

  // When the user approaches the end of unlocked pages, prefetch the next batch.
  useEffect(() => {
    const maxReachable = reachablePages[reachablePages.length - 1] ?? 1
    if (page >= maxReachable - 5) {
      void prefetchAhead()
    }
  }, [page, reachablePages, prefetchAhead])

  const scrollToPageBanner = () => {
    pageBannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const handleRefresh = () => {
    prefetchGenerationRef.current += 1
    prefetchInFlightRef.current = false
    fetchingPagesRef.current.clear()
    pageBookmarksRef.current = { 1: '' }
    pageCacheRef.current = {}
    hasMoreByPageRef.current = {}
    setReachablePages([1])
    setPage(1)
    void loadPage(1, true)
  }

  const handlePrevious = () => {
    if (page <= 1 || loading) return
    scrollToPageBanner()
    setPage((current) => current - 1)
  }

  const handleNext = () => {
    if (loading || !hasMore || records.length < PAGE_SIZE) return
    const nextPage = page + 1
    if (pageBookmarksRef.current[nextPage] === undefined) return
    scrollToPageBanner()
    setPage(nextPage)
  }

  const handlePageSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextPage = Number(event.target.value)
    if (!Number.isInteger(nextPage) || nextPage === page || loading) return
    if (nextPage > 1 && pageBookmarksRef.current[nextPage] === undefined) return
    scrollToPageBanner()
    setPage(nextPage)
  }

  const handleBackToFirst = () => {
    if (page === 1 || loading) return
    setPage(1)
  }

  const filteredRows = useMemo(() => {
    let next = [...records]

    if (filter !== 'all') {
      next = next.filter((row) => row.status === filter)
    }

    if (sortBy === 'credits') {
      next.sort((a, b) => b.credits - a.credits)
    } else if (sortBy === 'machine') {
      next.sort((a, b) => a.machineId - b.machineId)
    }

    // Pin hardcoded example at the top of page 1 (when filter allows it).
    if (page === 1 && (filter === 'all' || filter === EXAMPLE_LEDGER_ROW.status)) {
      next = [EXAMPLE_LEDGER_ROW, ...next.filter((row) => row.id !== EXAMPLE_LEDGER_ROW.id)]
    }

    return next
  }, [records, filter, sortBy, page])

  const stats = useMemo(() => {
    const totalCredits = records.reduce((sum, row) => sum + row.credits, 0)
    const verifiedCredits = records.filter((row) => row.status === 'verified').reduce((sum, row) => sum + row.credits, 0)
    const pendingCredits = records.filter((row) => row.status === 'pending').reduce((sum, row) => sum + row.credits, 0)
    return { totalCredits, verifiedCredits, pendingCredits, totalRecords: records.length }
  }, [records])

  useEffect(() => {
    onSummaryChange?.(stats)
  }, [onSummaryChange, stats])

  const toCsv = async () => {
    const payload = await fetchWatsonChainRecords({ refresh: false, paginated: false })
    const rows = payload.records

    const header = [
      'recordId',
      'date',
      'machineId',
      'brand',
      'barcode',
      'capacityMl',
      'bottleCount',
      'weightGrams',
      'points',
      'credits',
      'status',
      'txId',
      'txExplorerUrl',
      'platformNumber'
    ]

    const lines = rows.map((row) => [
      row.id,
      row.recordedAt,
      row.machineId,
      row.brand,
      row.barcode ?? '',
      row.capacityMl ?? '',
      row.bottleCount,
      row.weightGrams,
      row.points,
      row.credits,
      row.status,
      row.txId ?? '',
      row.txId ? getOneChainScanTxUrl(row.txId) : '',
      row.platformNumber ?? ''
    ].join(','))

    const csv = [header.join(','), ...lines].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `watson_chain_ledger_${new Date().toISOString().split('T')[0]}.csv`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  const getStatusIcon = (status: WatsonLedgerRow['status']) => {
    switch (status) {
      case 'verified':
        return <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
      case 'pending':
        return <ClockIcon className="h-4 w-4 text-yellow-500" />
      default:
        return <XCircleIcon className="h-4 w-4 text-red-500" />
    }
  }

  const getStatusBadge = (status: WatsonLedgerRow['status']) => {
    const styles = {
      verified: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      failed: 'bg-red-100 text-red-800 border-red-200'
    }
    return `px-2 py-1 text-xs font-medium rounded-full border ${styles[status]}`
  }

  const pageStart = filteredRows.length > 0 ? (page - 1) * PAGE_SIZE + 1 : 0
  const pageEnd = filteredRows.length > 0 ? pageStart + filteredRows.length - 1 : 0
  const firstRecordId = filteredRows[0]?.id
  const lastRecordId = filteredRows[filteredRows.length - 1]?.id

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-emerald-600">{stats.totalCredits.toLocaleString()}</div>
          <div className="text-xs text-emerald-700 font-medium">Credits (this page)</div>
        </div>
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{stats.verifiedCredits.toLocaleString()}</div>
          <div className="text-xs text-green-700 font-medium">Verified (this page)</div>
        </div>
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-yellow-600">{stats.pendingCredits.toLocaleString()}</div>
          <div className="text-xs text-yellow-700 font-medium">Pending (this page)</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 p-4 bg-gray-50 rounded-lg">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-4 w-4 text-gray-500" />
            <select
              value={filter}
              onChange={(event) => setFilter(event.target.value as typeof filter)}
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value="all">All Status</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <ChartBarIcon className="h-4 w-4 text-gray-500" />
            <select
              value={sortBy}
              onChange={(event) => setSortBy(event.target.value as typeof sortBy)}
              className="text-sm border border-gray-300 rounded px-2 py-1"
            >
              <option value="date">API order</option>
              <option value="credits">Sort by Credits</option>
              <option value="machine">Sort by Machine ID</option>
            </select>
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            className="flex items-center gap-2 text-sm px-3 py-2 border border-gray-300 rounded-lg hover:bg-white transition-colors"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
        <button
          type="button"
          onClick={() => void toCsv()}
          className="flex items-center gap-2 text-sm px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          Export all
        </button>
      </div>

      <div
        ref={pageBannerRef}
        className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800"
      >
        Page {page}: {firstRecordId ? `${firstRecordId} → ${lastRecordId}` : 'loading...'}.
        Next uses the OneChain <span className="font-mono">bookmark</span> from the previous response.
      </div>

      {(warning || source === 'fallback') && !error && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          {warning || 'The OneChain API could not be reached.'}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="mx-auto divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {[
                  { label: 'Record ID', width: 'w-36' },
                  { label: 'Date & Time', width: 'w-40' },
                  { label: 'Machine ID', width: 'w-28' },
                  { label: 'Coordinates', width: 'w-44' },
                  { label: 'Brand', width: 'w-40' },
                  { label: 'Bottles', width: 'w-20' },
                  { label: 'Weight (g)', width: 'w-24' },
                  { label: 'Points', width: 'w-20' },
                  { label: 'Credits', width: 'w-20' },
                  { label: 'Status', width: 'w-28' },
                  { label: 'Blockchain TX', width: 'w-28' }
                ].map((header) => (
                  <th
                    key={header.label}
                    className={`${header.width} py-3 align-middle text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap ${
                      header.label === 'Blockchain TX' ? 'px-2' : 'px-4'
                    }`}
                  >
                    {header.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-sm text-gray-500">
                    Loading Watson chain records...
                  </td>
                </tr>
              ) : filteredRows.map((row, index) => (
                <tr
                  key={`${page}-${row.id}`}
                  className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition-colors`}
                >
                  <td className="px-4 py-3 align-middle text-xs font-mono text-gray-800 font-medium">{row.id}</td>
                  <td className="px-4 py-3 align-middle text-sm text-gray-600">{formatDisplayDate(row.recordedAt)}</td>
                  <td className="px-4 py-3 align-middle text-sm text-gray-900 font-medium">{row.machineId || '—'}</td>
                  <td className="relative overflow-visible px-4 py-3 align-middle text-xs text-gray-600">
                    {row.coordinates
                      ? <CoordinatesCell coordinates={row.coordinates} />
                      : <span className="text-gray-400">N/A</span>}
                  </td>
                  <td className="px-4 py-3 align-middle text-sm text-gray-900">{row.brand}</td>
                  <td className="px-4 py-3 align-middle text-sm text-gray-700">{row.bottleCount}</td>
                  <td className="px-4 py-3 align-middle text-sm text-primary-600 font-bold">{row.weightGrams}</td>
                  <td className="px-4 py-3 align-middle text-sm text-gray-700">{row.points}</td>
                  <td className="px-4 py-3 align-middle text-sm text-emerald-600 font-bold">{row.credits}</td>
                  <td className="px-4 py-3 align-middle">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(row.status)}
                      <span className={getStatusBadge(row.status)}>{row.status}</span>
                    </div>
                  </td>
                  <td className="px-2 py-3 align-middle text-xs font-mono text-gray-500">
                    {row.txId ? (
                      <a
                        href={getOneChainScanTxUrl(row.txId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                        title={`View ${row.txId} on OneChainScan`}
                      >
                        <span className="truncate max-w-[4.5rem]">
                          {row.txId.slice(0, 6)}…{row.txId.slice(-3)}
                        </span>
                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 shrink-0" />
                      </a>
                    ) : (
                      <span className="text-gray-400">Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!loading && filteredRows.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <ChartBarIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-lg font-medium mb-2">No Watson chain records on this page</p>
              <p className="text-sm mb-4">
                {page > 1
                  ? 'Use Previous or Back to page 1 below to return to earlier records.'
                  : 'Try refreshing or adjusting your filters.'}
              </p>
              {page > 1 && (
                <button
                  type="button"
                  onClick={handleBackToFirst}
                  className="text-sm px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Back to page 1
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {!loading && (
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
          <span>
            {filteredRows.length > 0
              ? `Showing ${pageStart}-${pageEnd} on page ${page}`
              : `Page ${page} — no records`}
            {fetchedAt ? ` • Updated ${formatDisplayDate(fetchedAt)}` : ''}
            {!hasMore && filteredRows.length > 0 ? ' • Last page' : ''}
          </span>

          <div className="flex flex-nowrap items-center gap-2">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={page <= 1 || loading}
              aria-label="Previous"
              className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Previous</span>
            </button>
            <select
              value={page}
              onChange={handlePageSelect}
              disabled={loading || reachablePages.length <= 1}
              aria-label="Select page"
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white"
            >
              {reachablePages.map((pageNumber) => (
                <option key={pageNumber} value={pageNumber}>
                  Page {pageNumber}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleNext}
              disabled={!hasMore || loading || records.length < PAGE_SIZE}
              aria-label="Next"
              className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-white"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default CarbonCreditLedger
